import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";
import { 
  ArrowRight,
  Shield
} from "lucide-react";
import { MarketingNav, MarketingFooter } from "./LandingPage";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      <MarketingNav />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-4">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground">
              Last Updated: 2/20/2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] bg-white p-8 lg:p-12">
            <div className="prose prose-gray max-w-none">
              
              <h2 className="text-xl font-bold text-foreground mt-0 mb-4">
                1. Introduction
              </h2>
              <p className="text-muted-foreground mb-6">
                HeyIntake ("HeyIntake," "we," "our," or "us") is a product developed and operated by Okliko Inc., a Delaware-incorporated company.
              </p>
              <p className="text-muted-foreground mb-6">
                This Privacy Policy explains how we collect, use, store, and protect information when you use our website and services (the "Service").
              </p>
              <p className="text-muted-foreground mb-8">
                By using HeyIntake, you agree to the practices described in this Privacy Policy.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                2. Information We Collect
              </h2>
              <p className="text-muted-foreground mb-4">
                We collect the following types of information:
              </p>
              
              <h3 className="text-lg font-semibold text-foreground mt-6 mb-3">A. Account Information</h3>
              <ul className="text-muted-foreground mb-4 list-disc pl-6 space-y-1">
                <li>Name</li>
                <li>Email address</li>
                <li>Business name</li>
                <li>Billing information</li>
              </ul>

              <h3 className="text-lg font-semibold text-foreground mt-6 mb-3">B. Submission Data</h3>
              <p className="text-muted-foreground mb-2">When your customers complete intake forms, we process:</p>
              <ul className="text-muted-foreground mb-4 list-disc pl-6 space-y-1">
                <li>Contact details</li>
                <li>Form responses</li>
                <li>Uploaded information (if applicable)</li>
                <li>Metadata such as timestamps and IP address</li>
              </ul>

              <h3 className="text-lg font-semibold text-foreground mt-6 mb-3">C. Usage Data</h3>
              <p className="text-muted-foreground mb-2">We may collect:</p>
              <ul className="text-muted-foreground mb-8 list-disc pl-6 space-y-1">
                <li>Log data</li>
                <li>Browser type</li>
                <li>Device information</li>
                <li>Interaction with the Service</li>
              </ul>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                3. How We Use Information
              </h2>
              <p className="text-muted-foreground mb-2">We use collected information to:</p>
              <ul className="text-muted-foreground mb-4 list-disc pl-6 space-y-1">
                <li>Provide and operate the Service</li>
                <li>Process and store intake form submissions</li>
                <li>Generate AI-based summaries and lead scoring</li>
                <li>Enable pipeline tracking and reminders</li>
                <li>Provide customer support</li>
                <li>Improve platform performance and reliability</li>
                <li>Process subscription billing</li>
              </ul>
              <p className="text-primary font-medium mb-8">
                We do not sell user or submission data.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                4. AI Processing
              </h2>
              <p className="text-muted-foreground mb-4">
                HeyIntake uses automated systems to analyze intake form submissions for scoring and summarization.
              </p>
              <p className="text-muted-foreground mb-4">
                This processing is performed solely to provide functionality within the Service.
              </p>
              <p className="text-primary font-medium mb-8">
                We do not use your data to train public AI models.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                5. Data Storage and Security
              </h2>
              <p className="text-muted-foreground mb-4">
                We implement reasonable administrative, technical, and organizational safeguards to protect information.
              </p>
              <p className="text-muted-foreground mb-4">
                However, no system is completely secure. By using the Service, you acknowledge that data transmission carries inherent risks.
              </p>
              <p className="text-muted-foreground mb-8">
                Payment data is processed by Stripe and is not stored on HeyIntake servers.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                6. Data Retention
              </h2>
              <p className="text-muted-foreground mb-4">
                We retain account and submission data for as long as your account remains active.
              </p>
              <p className="text-muted-foreground mb-2">Upon account termination:</p>
              <ul className="text-muted-foreground mb-8 list-disc pl-6 space-y-1">
                <li>Data may be deleted within a reasonable period</li>
                <li>Backup copies may persist temporarily for system integrity</li>
              </ul>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                7. Data Sharing
              </h2>
              <p className="text-muted-foreground mb-2">We may share data only with:</p>
              <ul className="text-muted-foreground mb-4 list-disc pl-6 space-y-1">
                <li>Service providers (e.g., hosting, payment processing)</li>
                <li>Legal authorities if required by law</li>
                <li>In connection with a business transfer (e.g., merger or acquisition)</li>
              </ul>
              <p className="text-primary font-medium mb-8">
                We do not share or sell submission data for advertising.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                8. User Responsibilities
              </h2>
              <p className="text-muted-foreground mb-2">You are responsible for:</p>
              <ul className="text-muted-foreground mb-4 list-disc pl-6 space-y-1">
                <li>Ensuring you have permission to collect and process customer data</li>
                <li>Complying with applicable privacy laws in your jurisdiction</li>
              </ul>
              <p className="text-muted-foreground mb-8">
                HeyIntake acts as a service provider to your business.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                9. International Users
              </h2>
              <p className="text-muted-foreground mb-8">
                HeyIntake operates from the United States. By using the Service, you consent to data processing in the United States.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                10. Your Rights
              </h2>
              <p className="text-muted-foreground mb-2">Depending on your jurisdiction, you may have rights to:</p>
              <ul className="text-muted-foreground mb-4 list-disc pl-6 space-y-1">
                <li>Access your data</li>
                <li>Request correction</li>
                <li>Request deletion</li>
              </ul>
              <p className="text-muted-foreground mb-8">
                To make a request, contact: <a href="mailto:hello@heyintake.com" className="text-primary hover:underline">hello@heyintake.com</a>
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                11. Changes to This Policy
              </h2>
              <p className="text-muted-foreground mb-8">
                We may update this Privacy Policy from time to time. Continued use of the Service constitutes acceptance of the updated policy.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                12. Payment Processing
              </h2>
              <p className="text-muted-foreground mb-8">
                Payments are processed securely through Stripe, Inc. HeyIntake does not store full credit card numbers. Payment information is handled directly by Stripe in accordance with their Privacy Policy.
              </p>

              <h2 className="text-xl font-bold text-foreground mt-8 mb-4">
                13. Contact
              </h2>
              <p className="text-muted-foreground mb-2">
                For questions, contact: <a href="mailto:hello@heyintake.com" className="text-primary hover:underline">hello@heyintake.com</a>
              </p>
              <div className="text-muted-foreground mt-6 p-4 bg-muted/30 rounded-lg">
                <p className="font-semibold text-foreground">Okliko Inc.</p>
                <p>1000 Brickell Ave, STE 715</p>
                <p>Miami, FL 33131</p>
                <p><a href="mailto:hello@heyintake.com" className="text-primary hover:underline">hello@heyintake.com</a></p>
              </div>

            </div>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeIn}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to get started?
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Start your free trial today. No credit card required.
            </p>
            <Link to="/register">
              <Button size="lg" variant="secondary" className="rounded-full px-8 font-semibold">
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
